import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { queryPipelineStages, queryPipelines } from '@/features/pipelines/data';
import { buildStagePipelineFilters } from '@/features/pipelines/hooks/stagePageFilters';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

type UseStageFilterOptionsParams = {
  isAuthenticated: boolean;
  effectiveProjectId: string | null;
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  setSelectedStageNames: React.Dispatch<React.SetStateAction<string[]>>;
};

type UseStageFilterOptionsResult = {
  pipelineOptions: TableDropdownOption[];
  stageOptions: TableDropdownOption[];
};

export const useStageFilterOptions = ({
  isAuthenticated,
  effectiveProjectId,
  selectedOrganisationId,
  selectedPipelineIds,
  setSelectedStageNames,
}: UseStageFilterOptionsParams): UseStageFilterOptionsResult => {
  const pipelineOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/pipeline-options',
      selectedOrganisationId,
      effectiveProjectId,
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: [],
          }),
      ),
  });
  const pipelineOptions = useMemo(
    () =>
      (pipelineOptionsQuery.data?.items ?? [])
        .map((pipeline) => ({
          value: pipeline.id,
          label: pipeline.name,
          projectId: pipeline.projectId,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [pipelineOptionsQuery.data?.items],
  );

  const stageOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/stage-options',
      selectedOrganisationId,
      effectiveProjectId,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelineStages(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: buildStagePipelineFilters(selectedPipelineIds),
          }),
      ),
  });
  const stageOptions = useMemo(
    () =>
      Array.from(
        new Set((stageOptionsQuery.data?.items ?? []).map((stage) => stage.stageName.trim())),
      )
        .filter((stageName) => stageName.length > 0)
        .map((stageName) => ({ value: stageName, label: stageName }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [stageOptionsQuery.data?.items],
  );

  useEffect(() => {
    const availableStageNames = new Set(stageOptions.map((option) => option.value));
    setSelectedStageNames((current) =>
      current.filter((stageName) => availableStageNames.has(stageName)),
    );
  }, [setSelectedStageNames, stageOptions]);

  return { pipelineOptions, stageOptions };
};
